import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Spinner } from '@acorns/web-components'
import {
  Money,
  StyledRemoteString,
  useAnalyticsClient,
  useRemoteStrings,
} from '@acorns/web-utils'

import {
  trackRegistrationChooseSubscriptionButtonTappedPayload,
  trackRegistrationChooseSubscriptionComparePlansButtonTappedPayload,
  trackRegistrationChooseSubscriptionConfirmButtonTappedPayload,
  trackRegistrationChooseSubscriptionExpandBenefitsButtonTappedPayload,
  trackRegistrationChooseSubscriptionHideBenefitsButtonTappedPayload,
  trackRegistrationChooseSubscriptionScreenViewedPayload,
} from 'generated/trackingEvents'
import {
  PortfolioRiskLevel,
  TierKey,
  TierSubscriptionStatus,
  useSubscriptionPricingContentQuery,
} from 'generated/types'
import { reopenFlowTypeAnalyticsProps } from 'utils/analytics/reopenFlowAnalytics'

import { ComparePlansDrawer } from './ComparePlansDrawer'
import { formatTierPrice } from './formatter'
import { PlanCard } from './PlanCard'
import {
  ButtonFooterContainer,
  Cards,
  ComparePlansButton,
  Fade,
  FinePrint,
  PageContainer,
  ScrollContainer,
  ScrollContent,
  ScrollFadeWrapper,
  SpinnerContainer,
  StyledHeadline,
  StyledSubtitle,
} from './styled-components'
import { SubmitButton } from './SubmitButton'

interface Props {
  handleCtaPress: (tierId: string, tierPriceId: string) => void
  eventFunnelValue: string
  portfolioRisk?: PortfolioRiskLevel | undefined
  isLongReopen?: boolean
  loading?: boolean
}

export const DynamicPricingInterstitial = (props: Props) => {
  const analytics = useAnalyticsClient()
  const { getRemoteString } = useRemoteStrings()
  const [isComparePlansOpen, setIsComparePlansOpen] = useState(false)
  const [selectedTierIndex, setSelectedTierIndex] = useState<number | null>(
    null,
  )
  const [expandedPlans, setExpandedPlans] = useState<string[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showFade, setShowFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const [footerHeight, setFooterHeight] = useState(0)
  const footerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return
    }
    setFooterHeight(node.clientHeight)
  }, [])

  const { data: tierGroupData, loading: isTierGroupDataLoading } =
    useSubscriptionPricingContentQuery({ fetchPolicy: 'no-cache' })

  const sortedTiers = tierGroupData?.tierGroup.tierOptions
    .slice()
    .sort((a, b) => {
      return (
        b.preferredTierPrice.amountV2.value -
        a.preferredTierPrice.amountV2.value
      )
    })

  useEffect(() => {
    if (selectedTierIndex !== null || !sortedTiers) {
      return
    }

    const subscriptionHistory =
      tierGroupData?.tierSubscriptionHistoryByUserId?.edges
    const mostRecentActive = subscriptionHistory
      ?.filter((edge) => edge.status === TierSubscriptionStatus.Active)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0]

    let defaultIndex = 0
    if (mostRecentActive) {
      const matchingIndex = sortedTiers.findIndex(
        (tierOption) => tierOption.tier.key === mostRecentActive.tier.key,
      )
      if (matchingIndex !== -1) {
        defaultIndex = matchingIndex
      }
    }

    setSelectedTierIndex(defaultIndex)

    analytics.track(
      ...trackRegistrationChooseSubscriptionScreenViewedPayload({
        tierKey: sortedTiers[defaultIndex].tier.key,
        tierPrice: Money.toCompact(
          sortedTiers[defaultIndex].tier.tierPrice.amountV2.value,
        ).toString(),
        funnel: props.eventFunnelValue,
        ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
      }),
    )
  }, [
    sortedTiers,
    selectedTierIndex,
    tierGroupData?.tierSubscriptionHistoryByUserId?.edges,
    analytics,
  ])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }
    const handleScroll = () => {
      setShowBottomFade(
        container.scrollTop + container.clientHeight <
          container.scrollHeight - 1,
      )
      setShowFade(container.scrollTop > 0)
    }

    container.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [sortedTiers, expandedPlans])

  if (isTierGroupDataLoading || !sortedTiers || selectedTierIndex === null) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  const handleCardClick = (index: number) => {
    setSelectedTierIndex(index)

    const selectedTier = sortedTiers[index]
    const tierPrice = Money.toCompact(
      selectedTier.tier.tierPrice.amountV2.value,
    ).toString()

    analytics.track(
      ...trackRegistrationChooseSubscriptionButtonTappedPayload({
        tierKey: selectedTier.tier.key,
        tierPrice,
        ctaTitle: selectedTier.tier.name,
        funnel: props.eventFunnelValue,
        ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
      }),
    )

    analytics.track(
      ...trackRegistrationChooseSubscriptionScreenViewedPayload({
        tierKey: selectedTier.tier.key,
        tierPrice,
        funnel: props.eventFunnelValue,
        ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
      }),
    )
  }

  const handleExpand = (tierKey: TierKey) => {
    setExpandedPlans((prev) => {
      const isExpanding = !prev.includes(tierKey)
      const targetTier = sortedTiers.find(
        (tierOption) => tierOption.tier.key === tierKey,
      )

      if (targetTier) {
        const tierPrice = Money.toCompact(
          targetTier.tier.tierPrice.amountV2.value,
        ).toString()
        const eventFn = isExpanding
          ? trackRegistrationChooseSubscriptionExpandBenefitsButtonTappedPayload
          : trackRegistrationChooseSubscriptionHideBenefitsButtonTappedPayload
        analytics.track(
          ...eventFn({
            destination: '',
            tierKey: targetTier.tier.key,
            tierPrice,
            ctaTitle: targetTier.tier.name,
            funnel: props.eventFunnelValue,
            ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
          }),
        )
      }

      return isExpanding
        ? [...prev, tierKey]
        : prev.filter((key) => key !== tierKey)
    })
  }

  const handleSubmit = () => {
    const selectedTier = sortedTiers[selectedTierIndex].tier
    const ctaTitle = getRemoteString({
      key: 'registration.tier_selection.reopen_account.cta.primary',
    })

    analytics.track(
      ...trackRegistrationChooseSubscriptionConfirmButtonTappedPayload({
        destination: 'portfolio',
        tierKey: selectedTier.key,
        tierPrice: Money.toCompact(
          selectedTier.tierPrice.amountV2.value,
        ).toString(),
        ctaTitle,
        funnel: props.eventFunnelValue,
        ...(props.portfolioRisk && { portfolioRisk: props.portfolioRisk }),
        ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
      }),
    )

    props.handleCtaPress(selectedTier.id, selectedTier.tierPrice.id)
  }

  const handleComparePlansPress = () => {
    const selectedTier = sortedTiers[selectedTierIndex]
    const ctaTitle = getRemoteString({
      key: 'registration.tier_selection.reopen_account.cta.secondary',
    })

    // Track compare plans button tapped
    analytics.track(
      ...trackRegistrationChooseSubscriptionComparePlansButtonTappedPayload({
        destination: 'comparePlansDrawer',
        tierKey: selectedTier.tier.key,
        tierPrice: Money.toCompact(
          selectedTier.tier.tierPrice.amountV2.value,
        ).toString(),
        ctaTitle,
        funnel: props.eventFunnelValue,
        ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
      }),
    )

    setIsComparePlansOpen(true)
  }

  return (
    <PageContainer>
      <ScrollFadeWrapper offsetHeight={footerHeight}>
        <ScrollContainer ref={scrollContainerRef}>
          {showFade && <Fade position="top" />}
          <ScrollContent>
            <StyledHeadline>
              {getRemoteString({
                key: 'registration.tier_selection.reopen_account.headline',
              })}
            </StyledHeadline>
            <StyledSubtitle>
              {getRemoteString({
                key: 'registration.subscription.reopen_account.select_tier.body',
              })}
            </StyledSubtitle>
            <Cards>
              {sortedTiers.map((tierOption, index) => {
                const price = formatTierPrice({
                  tierPrice: tierOption.preferredTierPrice,
                })
                return (
                  <PlanCard
                    key={tierOption.tier.key}
                    tierKey={tierOption.tier.key}
                    name={tierOption.tier.name}
                    description={getRemoteString({
                      key: tierOption.tier.descriptionKey,
                    })}
                    dailyPrice={price.daily}
                    monthlyPrice={price.monthly}
                    selected={selectedTierIndex === index}
                    expanded={expandedPlans.includes(tierOption.tier.key)}
                    products={tierOption.tier.products}
                    matchProgram={tierOption.tier.matchProgram}
                    onClick={() => handleCardClick(index)}
                    onExpand={() => handleExpand(tierOption.tier.key)}
                  />
                )
              })}
            </Cards>
            <FinePrint>
              <StyledRemoteString stringKey="registration.tier_selection.fine_print" />
            </FinePrint>
          </ScrollContent>
          {showBottomFade && <Fade position="bottom" />}
        </ScrollContainer>
      </ScrollFadeWrapper>
      <ButtonFooterContainer ref={footerRef}>
        <SubmitButton handleCtaPress={handleSubmit} isLoading={props.loading}>
          {getRemoteString({
            key: 'registration.tier_selection.reopen_account.cta.primary',
          })}
        </SubmitButton>
        <ComparePlansButton styleType="flat" onPress={handleComparePlansPress}>
          {getRemoteString({
            key: 'registration.tier_selection.reopen_account.cta.secondary',
          })}
        </ComparePlansButton>
      </ButtonFooterContainer>
      <ComparePlansDrawer
        isOpen={isComparePlansOpen}
        setIsOpen={setIsComparePlansOpen}
        plans={sortedTiers}
        selectedTierIndex={selectedTierIndex}
        bankInterestRates={
          tierGroupData?.bankInterestRates?.__typename === 'BankInterestRates'
            ? tierGroupData.bankInterestRates.bankInterestRates
            : []
        }
        eventFunnelValue={props.eventFunnelValue}
        isLongReopen={props.isLongReopen}
      />
    </PageContainer>
  )
}
